import { type CSSProperties, type ReactNode, useCallback } from "react";

import { isElectron } from "../env";
import { cn } from "../lib/utils";
import {
  COMPOSER_COMPACT_MIN_LEFT_CONTROLS_WIDTH_PX,
  RIGHT_PANEL_INLINE_DEFAULT_WIDTH,
  RIGHT_PANEL_INLINE_LAYOUT_MEDIA_QUERY,
  RIGHT_PANEL_INLINE_SIDEBAR_MAX_WIDTH,
  RIGHT_PANEL_INLINE_SIDEBAR_MIN_WIDTH,
  RIGHT_PANEL_INLINE_SIDEBAR_WIDTH_STORAGE_KEY,
  normalizeRightPanelStoredWidth,
  useRightPanelInsetVarRef,
} from "../rightPanelLayout";
import {
  COMPOSER_FOOTER_RESERVE_ATTRIBUTE,
  measureComposerFooterReserve,
} from "./composerFooterLayout";
import { Sidebar, SidebarProvider, SidebarRail } from "./ui/sidebar";

export { RIGHT_PANEL_INLINE_LAYOUT_MEDIA_QUERY };

export function ChatRightPanelInlineSidebar(props: {
  open: boolean;
  onClose: () => void;
  /** Reopens the panel the slot was last showing (the rail handle asks for
   *  this, and it must reach the route so the URL agrees). */
  onRequestOpen: () => void;
  children: ReactNode;
}) {
  const { open, onClose, onRequestOpen } = props;
  // Body-portaled overlays (toasts) are fixed to the viewport, so they need to
  // be told how wide this slot is to keep off the panel.
  const insetRef = useRightPanelInsetVarRef();
  const onOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        onRequestOpen();
        return;
      }
      onClose();
    },
    [onClose, onRequestOpen],
  );
  const shouldAcceptInlineSidebarWidth = useCallback(
    ({ nextWidth, wrapper }: { nextWidth: number; wrapper: HTMLElement }) => {
      const composerForm = document.querySelector<HTMLElement>("[data-chat-composer-form='true']");
      if (!composerForm) return true;
      const composerViewport = composerForm.parentElement;
      if (!composerViewport) return true;
      const previousSidebarWidth = wrapper.style.getPropertyValue("--sidebar-width");
      wrapper.style.setProperty("--sidebar-width", `${nextWidth}px`);

      const viewportStyle = window.getComputedStyle(composerViewport);
      const viewportPaddingLeft = Number.parseFloat(viewportStyle.paddingLeft) || 0;
      const viewportPaddingRight = Number.parseFloat(viewportStyle.paddingRight) || 0;
      const viewportContentWidth = Math.max(
        0,
        composerViewport.clientWidth - viewportPaddingLeft - viewportPaddingRight,
      );
      const formRect = composerForm.getBoundingClientRect();
      const composerFooter = composerForm.querySelector<HTMLElement>(
        "[data-chat-composer-footer='true']",
      );
      // Room for the composer's fixed right-hand buttons. While a question or
      // an approval replaces the footer, keep what the footer last needed (it
      // comes back) or the question's answer buttons, whichever is wider;
      // otherwise the floor fell to the bare left reserve and squeezed both.
      const questionActions = composerForm.querySelector<HTMLElement>(
        "[data-chat-composer-question-actions='true']",
      );
      const composerRightReserve = composerFooter
        ? measureComposerFooterReserve(composerFooter)
        : Math.max(
            Number(composerForm.getAttribute(COMPOSER_FOOTER_RESERVE_ATTRIBUTE)) || 0,
            questionActions?.getBoundingClientRect().width ?? 0,
          );
      const minimumComposerWidth =
        COMPOSER_COMPACT_MIN_LEFT_CONTROLS_WIDTH_PX + composerRightReserve;
      const hasComposerOverflow = composerForm.scrollWidth > composerForm.clientWidth + 0.5;
      const overflowsViewport = formRect.width > viewportContentWidth + 0.5;
      const violatesMinimumComposerWidth = composerForm.clientWidth + 0.5 < minimumComposerWidth;

      if (previousSidebarWidth.length > 0) {
        wrapper.style.setProperty("--sidebar-width", previousSidebarWidth);
      } else {
        wrapper.style.removeProperty("--sidebar-width");
      }

      return !hasComposerOverflow && !overflowsViewport && !violatesMinimumComposerWidth;
    },
    [],
  );

  return (
    <SidebarProvider
      defaultOpen={false}
      open={open}
      onOpenChange={onOpenChange}
      className="w-auto min-h-0 flex-none bg-transparent"
      ref={insetRef}
      style={{ "--sidebar-width": RIGHT_PANEL_INLINE_DEFAULT_WIDTH } as CSSProperties}
    >
      <Sidebar
        side="right"
        collapsible="offcanvas"
        className={cn(
          "border-l border-border bg-rail text-foreground",
          isElectron && "pe-[var(--app-window-resize-edge-inset)]",
        )}
        motion="instant"
        resizable={{
          maxWidth: RIGHT_PANEL_INLINE_SIDEBAR_MAX_WIDTH,
          minWidth: RIGHT_PANEL_INLINE_SIDEBAR_MIN_WIDTH,
          normalizeStoredWidth: normalizeRightPanelStoredWidth,
          shouldAcceptWidth: shouldAcceptInlineSidebarWidth,
          storageKey: RIGHT_PANEL_INLINE_SIDEBAR_WIDTH_STORAGE_KEY,
        }}
      >
        {props.children}
        <SidebarRail />
      </Sidebar>
    </SidebarProvider>
  );
}
